import { formatDuration, telHref, type VisitDto } from '@kominiarz/shared';
import { Map as MapIcon, MapPin, Phone, Plus } from 'lucide-react';
import { Link } from 'react-router';
import { ColorDot } from '../../components/ColorDot';
import {
  UnpaidBadge,
  VisitStatusBadge,
  visitClientName,
  visitColor,
  visitPeople,
  visitTimeRange,
  visitTitle,
} from '../../components/VisitStatus';
import { local, todayLocal, type DateRange } from '../../lib/calendarDates';
import styles from './calendar.module.css';

interface AgendaViewProps {
  range: DateRange;
  visits: VisitDto[];
}

/** Day-by-day list: the default on phones, built for reading at a glance in the field. */
export function AgendaView({ range, visits }: AgendaViewProps) {
  const today = todayLocal();
  const tomorrow = local(today).plus({ days: 1 }).toISODate();
  const days: string[] = [];
  for (let d = range.start; d < range.end; d = d.plus({ days: 1 })) days.push(d.toISODate()!);

  const byDay = new Map<string, VisitDto[]>();
  for (const v of visits) {
    const day = local(v.startAt).toISODate()!;
    byDay.set(day, [...(byDay.get(day) ?? []), v]);
  }

  return (
    <div className={styles.agenda}>
      {days.map((day) => {
        const items = byDay.get(day) ?? [];
        const isToday = day === today;
        return (
          <section key={day} className={styles.day} aria-labelledby={`day-${day}`}>
            <h2 id={`day-${day}`} className={styles.dayHeader}>
              {isToday && <span className={styles.todayChip}>Dziś</span>}
              {day === tomorrow && <span className={styles.tomorrowChip}>Jutro</span>}
              <span className={styles.dayName}>{local(day).toFormat('EEEE, d MMMM')}</span>
              {items.some((v) => v.status !== 'cancelled') && (
                <Link to={`/mapa?data=${day}`} className={styles.dayMap}>
                  <MapIcon aria-hidden="true" />
                  Mapa
                </Link>
              )}
            </h2>
            {items.length === 0 ? (
              <p className={styles.dayEmpty}>
                {isToday ? 'Brak wizyt na dziś.' : 'Brak wizyt.'}{' '}
                <Link to={`/wizyty/nowa?data=${day}`} className={styles.dayEmptyLink}>
                  <Plus aria-hidden="true" />
                  Dodaj wizytę
                </Link>
              </p>
            ) : (
              <ul className={styles.visitList}>
                {items.map((v) => (
                  <AgendaItem key={v.id} visit={v} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function AgendaItem({ visit: v }: { visit: VisitDto }) {
  const client = visitClientName(v);
  const cancelled = v.status === 'cancelled';
  return (
    <li className={cancelled ? `${styles.item} ${styles.itemCancelled}` : styles.item}>
      <Link to={`/wizyty/${v.id}`} className={styles.itemLink} style={{ borderLeftColor: `var(--user-${visitColor(v)})` }}>
        {v.arrivalWindowMinutes ? (
          <span className={styles.itemTime}>
            <span className={styles.itemStart}>{visitTimeRange(v)}</span>
            <span className={styles.itemWindow}>przyjazd, {formatDuration(v.durationMinutes)} pracy</span>
          </span>
        ) : (
          <span className={styles.itemTime}>
            <span className={styles.itemStart}>{local(v.startAt).toFormat('HH:mm')}</span>
            <span className={styles.itemEnd}>{local(v.endAt).toFormat('HH:mm')}</span>
          </span>
        )}
        <span className={styles.itemTitle}>
          {visitTitle(v)}
          <VisitStatusBadge status={v.status} />
          <UnpaidBadge visit={v} />
        </span>
        {client && <span className={styles.itemClient}>{client}</span>}
        {v.address && (
          <span className={styles.itemAddress}>
            <MapPin aria-hidden="true" />
            {v.address}
          </span>
        )}
        <span className={styles.itemAssignee}>
          {v.assignees.map((u) => (
            <ColorDot key={u.id} color={u.color} size="sm" />
          ))}
          {visitPeople(v)}
        </span>
      </Link>
      {v.phone && !cancelled && (
        <a className={styles.itemCall} href={telHref(v.phone)}>
          <Phone aria-hidden="true" />
          <span>Zadzwoń</span>
        </a>
      )}
    </li>
  );
}
